import { useCallback, useEffect, useRef, useState, type DependencyList, type Dispatch, type SetStateAction } from 'react';
import i18n from 'i18next';

interface UseAsyncDataOptions<T> {
  initial?: T;
  fallbackError?: string;
  enabled?: boolean;
}

interface UseAsyncDataResult<T> {
  data: T | undefined;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  setData: Dispatch<SetStateAction<T | undefined>>;
}

const defaultFallbackError = () => i18n.t('common.dataLoadError', { defaultValue: 'Error al cargar los datos' });

export function useAsyncData<T>(
  fetchFn: () => Promise<T>,
  deps: DependencyList,
  opts?: UseAsyncDataOptions<T>,
): UseAsyncDataResult<T> {
  const { initial, fallbackError, enabled = true } = opts ?? {};
  const [data, setData] = useState<T | undefined>(initial);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);

  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;

  const fallbackRef = useRef(fallbackError);
  fallbackRef.current = fallbackError;

  const execute = useCallback(async (signal: { cancelled: boolean }) => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFnRef.current();
      if (!signal.cancelled) setData(result);
    } catch (err) {
      if (signal.cancelled) return;
      setError(
        err instanceof Error && err.message
          ? err.message
          : fallbackRef.current ?? defaultFallbackError(),
      );
    } finally {
      if (!signal.cancelled) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const signal = { cancelled: false };
    execute(signal);
    return () => {
      signal.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled]);

  const refetch = useCallback(async () => {
    await execute({ cancelled: false });
  }, [execute]);

  return { data, loading, error, refetch, setData };
}
